import { CALGARY_TIME_ZONE } from './calgaryTime';

export type TimestampFormat = 'date-time' | 'date' | 'time' | 'relative';
export type Timezone = string;

/** Format an ISO timestamp in Calgary time (MST or MDT as appropriate). */
export const formatTimestamp = (isoString: string, format: TimestampFormat = 'date-time'): string =>
  formatTimestampWithTimezone(isoString, CALGARY_TIME_ZONE, format);

/** Format the instant directly; never reparse a locale string in the browser's zone. */
export const formatTimestampWithTimezone = (
  isoString: string,
  timezone: Timezone = CALGARY_TIME_ZONE,
  format: TimestampFormat = 'date-time'
): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) throw new Error('Invalid date');
    if (format === 'relative') {
      const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
      if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    }
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }).formatToParts(date);
    const part = (type: string) => parts.find(p => p.type === type)!.value;
    const day = `${part('month')}/${part('day')}/${part('year')}`;
    const time = `${part('hour')}:${part('minute')} ${part('dayPeriod')}`;
    if (format === 'date') return day;
    if (format === 'time') return time;
    return `${day} - ${time}`;
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid Date';
  }
};

/** Keep stored/transmitted instants in UTC; timezone conversion is for display. */
export const getCurrentTimestamp = (): string => new Date().toISOString();

/** Legacy name retained for callers; displays Calgary time, not fixed UTC−7. */
export const toMST = (isoString: string): string => formatTimestamp(isoString);
